import React from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { EvaluationsListView } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationsListView';
import { EvaluationView } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationView/EvaluationView';
import { useSearchParams } from 'react-router-dom';
import { EvaluationService } from 'services/evaluation-service';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { NotificationService } from 'services/notification-service';
import { EVALUATION_REFETCH_INTERVAL, anyEvaluationHasRunningTests } from 'shared/constants/evaluation';
import { EVALUATIONS_QUERY_KEY } from 'shared/constants/query-keys';
import { Evaluation } from 'shared/interfaces/Evaluation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface EvaluationsSectionProps {
  smartAssistant: SmartAssistant;
  isGuest?: boolean;
}

type RunEvaluationTestParams = {
  evaluationTestIds: string[];
  evaluationId: string;
};

export function EvaluationsSection({ smartAssistant, isGuest = false }: EvaluationsSectionProps) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const evaluationId = searchParams.get('evaluationId');

  const evaluationsQuery = useQuery({
    queryKey: [EVALUATIONS_QUERY_KEY, smartAssistant._id],
    queryFn: () =>
      (isGuest ? GuestSmartAssistantService : EvaluationService).getEvaluationsForSmartAssistant(smartAssistant._id),
    // Refetch the evaluations if there is a running test in the currently selected evaluation
    refetchInterval: (evaluations) => {
      if (evaluations && anyEvaluationHasRunningTests(evaluations)) return EVALUATION_REFETCH_INTERVAL;
      return false;
    }
  });

  const runEvaluationTestsMutation = useMutation({
    mutationFn: ({ evaluationTestIds, evaluationId }: RunEvaluationTestParams) =>
      EvaluationService.runEvaluation(evaluationId, { evaluationTestIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATIONS_QUERY_KEY, smartAssistant._id] });
    },
    onError: () => {
      NotificationService.showResult(false, 'FEED_BACK.EVALUATION.FAILURE.RUN');
    }
  });

  const handleRunAllTests = (evaluationId: string) => {
    const emptyIdsToForceRunAll: string[] = [];
    runEvaluationTestsMutation.mutate({ evaluationTestIds: emptyIdsToForceRunAll, evaluationId });
  };

  const handleRunSpecificTests = (evaluationTestIds: string[], evaluationId: string) => {
    runEvaluationTestsMutation.mutate({ evaluationId, evaluationTestIds });
  };

  const handleViewEvaluation = (evaluation: Evaluation) => {
    searchParams.set('evaluationId', evaluation._id);
    setSearchParams(searchParams);
  };

  const handleCloseEvaluationView = () => {
    searchParams.delete('evaluationId');
    setSearchParams(searchParams);
  };

  const evaluations = evaluationsQuery.data || [];
  const selectedEvaluation = evaluationId ? evaluations.find((evaluation) => evaluation._id === evaluationId) : null;

  if (evaluationsQuery.isError) return <ErrorAlert />;
  if (evaluationsQuery.isLoading) return <FullScreenLoader />;

  return selectedEvaluation ? (
    <EvaluationView
      evaluation={selectedEvaluation}
      smartAssistant={smartAssistant}
      onBack={handleCloseEvaluationView}
      handleRunAllTests={handleRunAllTests}
      handleRunSpecificTests={handleRunSpecificTests}
      isGuest={isGuest}
    />
  ) : (
    <EvaluationsListView
      smartAssistant={smartAssistant}
      evaluations={evaluations}
      onEvaluationClick={handleViewEvaluation}
      handleRunAllTests={handleRunAllTests}
      isGuest={isGuest}
    />
  );
}
